import { useMutation, useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import {
  getEgovApplicationStepsByTag,
  getYearEndAdjustmentDocumentTabCounts,
  getYearEndAdjustmentInstancesByTag,
  getGovernmentApplicationPackageInstances,
  getOperationPackageInstances,
  getOperationPackagePatterns,
  getOperationPackages,
  getOperationTaskCountInProgress,
  getPackageInstanceTag,
  postOperationPackagePatterns,
} from '@/features/operation-packages/api';
import type {
  EgovApplicationStepsByTagQuery,
  YearEndAdjustmentDocumentTabCountsQuery,
  YearEndAdjustmentInstancesByTagQuery,
  OwnerProduct,
  PackageType,
} from '@/features/operation-packages/api';
import { mapGovernmentApplicationInstanceToTableRowData } from '@/features/operation-packages/mapGovernmentApplicationInstanceToTableRowData';
import { QUERY_PRESETS } from '@/lib/query';
import { queryClient } from '@/lib/queryClient';

// --- Query Keys ---

export const operationPackageQueryKeys = {
  all: (ownerProduct: OwnerProduct) =>
    ['operation-packages', ownerProduct] as const,
  lists: (ownerProduct: OwnerProduct) =>
    [...operationPackageQueryKeys.all(ownerProduct), 'list'] as const,
  list: (ownerProduct: OwnerProduct) =>
    [...operationPackageQueryKeys.lists(ownerProduct)] as const,
  countInProgress: (ownerProduct: OwnerProduct, id: string) =>
    [
      ...operationPackageQueryKeys.all(ownerProduct),
      id,
      'count-in-progress',
    ] as const,
  patterns: (ownerProduct: OwnerProduct, id: string) =>
    [...operationPackageQueryKeys.all(ownerProduct), id, 'patterns'] as const,
  patternCountInProgress: (
    ownerProduct: OwnerProduct,
    id: string,
    patternId: string,
  ) =>
    [
      ...operationPackageQueryKeys.patterns(ownerProduct, id),
      patternId,
      'count-in-progress',
    ] as const,
  instances: (
    ownerProduct: OwnerProduct,
    patternId: string,
    page?: number,
    pageSize?: number,
    targetId?: string,
  ) =>
    [
      ...operationPackageQueryKeys.all(ownerProduct),
      'instances',
      patternId,
      page,
      pageSize,
      targetId,
    ] as const,
  governmentApplicationInstances: (
    ownerProduct: OwnerProduct,
    patternId: string,
    page?: number,
    pageSize?: number,
    targetId?: string,
  ) =>
    [
      ...operationPackageQueryKeys.all(ownerProduct),
      'government-application-instances',
      patternId,
      page,
      pageSize,
      targetId,
    ] as const,
  /** 行政申請確認一覧（タグ別）の invalidate 用プレフィックス */
  egovApplicationStepsByTagRoot: (ownerProduct: OwnerProduct, tagId: string) =>
    [
      ...operationPackageQueryKeys.all(ownerProduct),
      'egov-confirmation-steps',
      tagId,
    ] as const,
  egovApplicationStepsByTag: (
    ownerProduct: OwnerProduct,
    tagId: string,
    filters: {
      page?: number;
      pageSize?: number;
      orderBy?: string;
      orderDirection?: string;
      searchText?: string;
      status?: readonly string[];
    },
  ) =>
    [
      ...operationPackageQueryKeys.egovApplicationStepsByTagRoot(
        ownerProduct,
        tagId,
      ),
      filters.page,
      filters.pageSize,
      filters.orderBy,
      filters.orderDirection,
      filters.searchText,
      filters.status,
    ] as const,
  yearEndAdjustmentInstancesByTagRoot: (
    ownerProduct: OwnerProduct,
    tagId: string,
  ) =>
    [
      ...operationPackageQueryKeys.all(ownerProduct),
      'year-end-adjustment-instances',
      tagId,
    ] as const,
  yearEndAdjustmentInstancesByTag: (
    ownerProduct: OwnerProduct,
    tagId: string,
    filters: {
      page?: number;
      pageSize?: number;
      orderBy?: string;
      orderDirection?: string;
      searchText?: string;
      status?: readonly string[];
      documentFocus?: string;
    },
  ) =>
    [
      ...operationPackageQueryKeys.yearEndAdjustmentInstancesByTagRoot(
        ownerProduct,
        tagId,
      ),
      filters.page,
      filters.pageSize,
      filters.orderBy,
      filters.orderDirection,
      filters.searchText,
      filters.status,
      filters.documentFocus,
    ] as const,
  yearEndAdjustmentDocumentTabCounts: (
    ownerProduct: OwnerProduct,
    tagId: string,
    filters: {
      searchText?: string;
      status?: readonly string[];
    },
  ) =>
    [
      ...operationPackageQueryKeys.yearEndAdjustmentInstancesByTagRoot(
        ownerProduct,
        tagId,
      ),
      'document-tab-counts',
      filters.searchText,
      filters.status,
    ] as const,
  packageInstanceTag: (ownerProduct: OwnerProduct, tagId: string) =>
    [
      ...operationPackageQueryKeys.all(ownerProduct),
      'instance-tag',
      tagId,
    ] as const,
} as const;

// --- Hooks ---

/**
 * Operation Packageの一覧を取得する
 */
export const useGetOperationPackages = (ownerProduct: OwnerProduct) => {
  const { data, isLoading, error } = useQuery({
    queryKey: operationPackageQueryKeys.list(ownerProduct),
    queryFn: () => getOperationPackages(ownerProduct),
  });
  return { data, isLoading, error };
};

/**
 * 指定されたパッケージタイプのパッケージIDを取得するReact Queryフック
 * @param ownerProduct プロダクト
 * @param packageType 取得したいパッケージのタイプ
 */
export function useOperationPackageIdByType(
  ownerProduct: OwnerProduct,
  packageType: PackageType,
) {
  // 全パッケージを取得
  const {
    data: allPackages,
    error,
    isLoading,
  } = useGetOperationPackages(ownerProduct);

  // パッケージタイプに対応するIDをメモ化して算出
  const packageId = useMemo(() => {
    if (!allPackages || !packageType) return null;

    const targetPackage = allPackages.find(
      (pkg) => pkg.packageType === packageType,
    );
    if (targetPackage) {
      return targetPackage.operationPackageId;
    }
    return null;
  }, [allPackages, packageType]);

  return {
    data: packageId,
    error,
    isLoading,
    isSuccess: !isLoading && !error,
    isError: !!error,
    isLoadingError: !!error,
  };
}

/**
 * Operation Packageのパターンの定義一覧を取得する
 */
export const useGetOperationPackagePatterns = (
  ownerProduct: OwnerProduct,
  id: string,
  options?: { enabled?: boolean },
) => {
  const { data, isLoading, error } = useSuspenseQuery({
    queryKey: operationPackageQueryKeys.patterns(ownerProduct, id),
    queryFn: () => {
      if (options?.enabled === false || !id) {
        return Promise.resolve([]);
      }
      return getOperationPackagePatterns(ownerProduct, id);
    },
  });
  return { data, isLoading, error };
};

/**
 * 特定のOperation Packageのパターンを作成する
 */
export const usePostOperationPackagePatterns = (
  ownerProduct: OwnerProduct,
  id: string,
  options?: {
    onSuccess?: () => void;
    onError?: (error: Error) => void;
  },
) => {
  const { mutate, isPending, error } = useMutation({
    mutationFn: () => postOperationPackagePatterns(ownerProduct, id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: operationPackageQueryKeys.patterns(ownerProduct, id),
      });
      options?.onSuccess?.();
    },
    onError: (error) => {
      options?.onError?.(error);
    },
  });
  return { mutate, isPending, error };
};

/**
 * 特定のOperation Packageのパターンのタスクの実行中の数を取得する
 */
export const useGetOperationTaskCountInProgress = ({
  ownerProduct,
  operationPackageId: id,
  patternId,
}: {
  ownerProduct: OwnerProduct;
  operationPackageId: string;
  patternId: string;
}) => {
  const { data, isLoading, error } = useQuery({
    queryKey: operationPackageQueryKeys.patternCountInProgress(
      ownerProduct,
      id,
      patternId,
    ),
    queryFn: () => getOperationTaskCountInProgress(ownerProduct, id, patternId),
  });
  return { data, isLoading, error };
};

/**
 * 特定のOperation Packageのパターンのインスタンスの一覧を取得する
 */
export const useGetOperationPackageInstances = (
  ownerProduct: OwnerProduct,
  patternId: string,
  options?: {
    enabled?: boolean;
    page?: number;
    pageSize?: number;
    targetId?: string;
  },
) => {
  const page = options?.page ?? 1;
  const pageSize = options?.pageSize ?? 50;
  const targetId = options?.targetId;
  const { data, isPending, error } = useQuery({
    queryKey: operationPackageQueryKeys.instances(
      ownerProduct,
      patternId,
      page,
      pageSize,
      targetId,
    ),
    queryFn: () =>
      getOperationPackageInstances(ownerProduct, patternId, {
        page,
        pageSize,
        targetId,
      }),
    enabled: options?.enabled ?? true,
  });
  return { data, isPending, error };
};

/**
 * Labor 行政申請向けインスタンス一覧（`GET .../instances/with-step-columns`、Step 列付き）
 */
export const useGetGovernmentApplicationPackageInstances = (
  ownerProduct: OwnerProduct,
  patternId: string,
  options?: {
    enabled?: boolean;
    page?: number;
    pageSize?: number;
    targetId?: string;
  },
) => {
  const page = options?.page ?? 1;
  const pageSize = options?.pageSize ?? 50;
  const targetId = options?.targetId;
  const { data, isPending, error } = useQuery({
    queryKey: operationPackageQueryKeys.governmentApplicationInstances(
      ownerProduct,
      patternId,
      page,
      pageSize,
      targetId,
    ),
    queryFn: () =>
      getGovernmentApplicationPackageInstances(ownerProduct, patternId, {
        page,
        pageSize,
        targetId,
      }),
    select: (response) => ({
      ...response,
      items: response.items.map((row) =>
        mapGovernmentApplicationInstanceToTableRowData(row),
      ),
    }),
    enabled: options?.enabled ?? true,
  });
  return { data, isPending, error };
};

/**
 * 行政申請「確認画面」一覧（タグ別 Step 行）
 */
export const useGetEgovApplicationStepsByTag = (
  ownerProduct: OwnerProduct,
  tagId: string,
  options?: {
    enabled?: boolean;
  } & Omit<EgovApplicationStepsByTagQuery, 'tagId'>,
) => {
  const enabled = options?.enabled ?? true;
  const page = options?.page ?? 1;
  const pageSize = options?.pageSize ?? 50;
  const orderBy = options?.orderBy;
  const orderDirection = options?.orderDirection;
  const searchText = options?.searchText;
  const status = options?.status;
  const { data, isPending, error } = useQuery({
    queryKey: operationPackageQueryKeys.egovApplicationStepsByTag(
      ownerProduct,
      tagId,
      {
        page,
        pageSize,
        orderBy,
        orderDirection,
        searchText,
        status,
      },
    ),
    queryFn: () =>
      getEgovApplicationStepsByTag(tagId, {
        page,
        pageSize,
        orderBy,
        orderDirection: orderDirection ?? 'asc',
        searchText,
        status,
      }),
    enabled: enabled && Boolean(tagId),
  });
  return { data, isPending, error };
};

/**
 * 年末調整 内容確認一覧（タグ別インスタンス行）
 *
 * NOTE: mock フェーズでは未使用。一覧画面は
 * `routes/_staff/labor-core/year-end-adjustment-confirmation/-query/api.ts`
 * の route-local mock 層を使用しており、バックエンド API 提供後にそちらの
 * queryFn から本フック / `api.ts` の取得関数へ切り替える。
 */
export const useGetYearEndAdjustmentInstancesByTag = (
  ownerProduct: OwnerProduct,
  tagId: string,
  options?: {
    enabled?: boolean;
    meta?: { disableGlobalErrorHandling?: boolean };
  } & Omit<YearEndAdjustmentInstancesByTagQuery, 'tagId'>,
) => {
  const enabled = options?.enabled ?? true;
  const page = options?.page ?? 1;
  const pageSize = options?.pageSize ?? 50;
  const orderBy = options?.orderBy;
  const orderDirection = options?.orderDirection;
  const searchText = options?.searchText;
  const status = options?.status;
  const documentFocus = options?.documentFocus;
  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: operationPackageQueryKeys.yearEndAdjustmentInstancesByTag(
      ownerProduct,
      tagId,
      {
        page,
        pageSize,
        orderBy,
        orderDirection,
        searchText,
        status,
        documentFocus,
      },
    ),
    queryFn: () =>
      getYearEndAdjustmentInstancesByTag(tagId, {
        page,
        pageSize,
        orderBy,
        orderDirection: orderDirection ?? 'asc',
        searchText,
        status,
        documentFocus,
      }),
    ...QUERY_PRESETS.list(),
    enabled: enabled && Boolean(tagId),
    meta: options?.meta,
  });
  return { data, isPending, isError, error, refetch };
};

/**
 * 年末調整 内容確認 書類タブ別件数（searchText / status は一覧と同期）
 *
 * NOTE: mock フェーズでは未使用。一覧画面は
 * `routes/_staff/labor-core/year-end-adjustment-confirmation/-query/api.ts`
 * の route-local mock 層を使用しており、バックエンド API 提供後にそちらの
 * queryFn から本フック / `api.ts` の取得関数へ切り替える。
 */
export const useGetYearEndAdjustmentDocumentTabCounts = (
  ownerProduct: OwnerProduct,
  tagId: string,
  options?: {
    enabled?: boolean;
    meta?: { disableGlobalErrorHandling?: boolean };
  } & Omit<YearEndAdjustmentDocumentTabCountsQuery, 'tagId'>,
) => {
  const enabled = options?.enabled ?? true;
  const searchText = options?.searchText;
  const status = options?.status;
  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: operationPackageQueryKeys.yearEndAdjustmentDocumentTabCounts(
      ownerProduct,
      tagId,
      {
        searchText,
        status,
      },
    ),
    queryFn: () =>
      getYearEndAdjustmentDocumentTabCounts(tagId, {
        searchText,
        status,
      }),
    ...QUERY_PRESETS.list(),
    enabled: enabled && Boolean(tagId),
    meta: options?.meta,
  });
  return { data, isPending, isError, error, refetch };
};

/**
 * Operation Package Instance タグ（タグ ID から id / name を取得）
 */
export const useGetPackageInstanceTag = (
  ownerProduct: OwnerProduct,
  tagId: string,
  options?: { enabled?: boolean },
) => {
  const { data, isPending, error } = useQuery({
    queryKey: operationPackageQueryKeys.packageInstanceTag(ownerProduct, tagId),
    queryFn: () => getPackageInstanceTag(ownerProduct, tagId),
    enabled: (options?.enabled ?? true) && Boolean(tagId),
  });
  return { data, isPending, error };
};
